import * as R from 'remeda';
import {
  getRectangleCorners,
  getVectorDifference,
  getVectorSize,
  getWrappingRectangle,
  matrixTransform,
  origin,
  type Padding,
  type Point2D,
  type Rectangle2D,
  unit,
  type Vector2D,
} from '../utils';
import type { CanvasTransform } from './types';

// ============================================================================
// Low-level matrix builders (internal)
// Convention: Upward transforms (toward viewport/screen, used for rendering) are explicit.
// Downward transforms (toward content, used for event handling) use .inverse().
// ============================================================================

export const matrices = {
  screen: {
    // Screen → Viewport: inverse of viewport.toScreen (for event handling)
    toViewport: (rect: Rectangle2D): DOMMatrixReadOnly =>
      matrices.viewport.toScreen(rect).inverse(),
  },

  viewport: {
    // Viewport → Screen: add viewport's position (for rendering)
    toScreen: (rect: Rectangle2D): DOMMatrixReadOnly =>
      new DOMMatrix([1, 0, 0, 1, rect.x, rect.y]),

    // Viewport → Canvas: inverse of canvas.toViewport (for event handling)
    toCanvas: (canvasTransform: CanvasTransform): DOMMatrixReadOnly =>
      matrices.canvas.toViewport(canvasTransform).inverse(),
  },

  canvas: {
    // Canvas → Viewport: apply zoom and translation (for rendering)
    toViewport: (canvasTransform: CanvasTransform): DOMMatrixReadOnly =>
      new DOMMatrix([
        canvasTransform.zoom,
        0,
        0,
        canvasTransform.zoom,
        canvasTransform.translateX,
        canvasTransform.translateY,
      ]),

    // Canvas → Content: inverse of content.toCanvas (for event handling)
    toContent: (
      contentPadding: Padding,
      canvasTransform: CanvasTransform
    ): DOMMatrixReadOnly =>
      matrices.content.toCanvas(contentPadding, canvasTransform).inverse(),
  },

  content: {
    // Content → Canvas: add padding (scaled to canvas units) (for rendering)
    toCanvas: (
      contentPadding: Padding,
      canvasTransform: CanvasTransform
    ): DOMMatrixReadOnly =>
      new DOMMatrix([
        1,
        0,
        0,
        1,
        contentPadding.left / canvasTransform.zoom,
        contentPadding.top / canvasTransform.zoom,
      ]),
  },
};

export const composeMatrices = (
  ...ms: DOMMatrixReadOnly[]
): DOMMatrixReadOnly => ms.reduce((acc, m) => m.multiply(acc), new DOMMatrix());

// Read as "apply matrix transform m to entity p/v/s/r"
export const apply = {
  point:
    (m: DOMMatrixReadOnly) =>
    (p: Point2D): Point2D => {
      return R.pipe(p, matrixTransform(m));
    },

  vector:
    (m: DOMMatrixReadOnly) =>
    (v: Vector2D): Vector2D => {
      // TODO: I don't like using Vector2D and Point2D interchangeably :thinking:
      return R.pipe([v, origin()] as const, R.map(matrixTransform(m)), points =>
        getVectorDifference(...points)
      );
    },

  scalar:
    (m: DOMMatrixReadOnly) =>
    (s: number): number => {
      return R.pipe(unit(), apply.vector(m), getVectorSize, size => size * s);
    },

  rect:
    (m: DOMMatrixReadOnly) =>
    (r: Rectangle2D): Rectangle2D =>
      // TODO: If we want to support non-axis-aligned rectangles, we'd need
      // something else than `getBoundingBox`
      R.pipe(
        r,
        getRectangleCorners,
        R.map(apply.point(m)),
        getWrappingRectangle
      ),
};
